/*
 * SCR-012 Brand Hub controller — Phase A1. Read-only unless the section's `editing` setting is on (data-editing="true"): R-7,
 * the brand never edits and there are no roles, so by default no draft is read, no chip, meter or bar is rendered and nothing
 * is saved or sent; the carousel, try-on, look panel, favorites, likes and sorts work the same in both modes. Reads and writes only through NcApp.gateway() (the mock in A1): listBrands
 * (the carousel), getBrandHub (what the hub shows = the candidates to feature), getBrandHubDraft (the saved selection),
 * saveBrandHubDraft and publishBrandHub (the two distinct actions), getAuthState, getAsset, setFavorite, setLookLike,
 * setLookSaved. The canvas, side tools, model selector and look panel (getCurrentLook / removeLookItem, add, undo, redo, clear)
 * are the <nc-fitting-room> component (preset brand-hub); this screen relays its `nc-fitting:connect` and `:tool` as
 * `nc-brand-hub:connect` and `:tool`. No network, no persistence, no images of its own.
 * "Probar" (2026-10-25): each product card has the probador's chooser chip ([data-bh-try] nc-chip, data-value = product id). It runs the
 * SAME code path as the probador grid (NcFittingRoomCore.syncChooserChips / toggleChooserChip -> room.toggle): Probar / Cambiar (slot
 * taken, the accessible name says which garment it replaces) / En tu look, one garment per slot with the replacement toast + Deshacer.
 * The chips follow the room's `nc-fitting:change`. In editing mode the "Destacado" chip stays a separate control.
 *
 * Editing model (PROPOSED from the gateway contract, see the section header): a draft is { brandId, featuredProductIds,
 * collectionIds, communityLookIds }. Each candidate card carries a chip ("Destacado" / "Incluido" / the collection name) that
 * adds it to or removes it from the selection. "Guardar borrador" saves the selection (only the upper limits are enforced);
 * "Enviar cambios" validates the limits and the minimum of featured products and sends it. The copy says "enviado", never
 * "publicado": moderation and timing are UNKNOWN. Validation runs on the two actions, never per keystroke or toggle, and errors
 * are inline (role=alert, linked with aria-describedby) with focus on the first invalid group. A failed save or send keeps the
 * selection and the same button retries; a 409 conflict offers "Recargar hub" (drops the local selection of that brand). Unsaved
 * selections are kept per brand in memory while switching brands in the carousel, and the status line says what is true:
 * unsaved changes / draft saved / changes sent. Authentication or permission errors on read show a recoverable panel.
 * A request sequence guards against stale responses. Navigation (open, connect, upload) is Phase B: events only.
 * Pure helpers are exported for node:test.
 */
(function () {
  // "{n} de {m} prendas" with { n: 2, m: 5 }
  function fill(pattern, vars) {
    return String(pattern || '').replace(/\{(\w+)\}/g, function (all, k) { return vars[k] === undefined ? all : String(vars[k]); });
  }

  // "{n} prenda/{n} prendas": the first form for exactly 1, the second otherwise
  function plural(pattern, n) {
    var parts = String(pattern || '').split('/');
    return parts.length > 1 && n !== 1 ? parts[1] : parts[0];
  }

  // ids stay unique and keep their order of selection
  function setMember(list, id, on) {
    var has = list.indexOf(id) >= 0;
    if (on && !has) return list.concat([id]);
    if (!on && has) return list.filter(function (x) { return x !== id; });
    return list;
  }

  // selection of a draft restricted to what the hub can actually show (stale ids are dropped)
  function applyDraft(draft, candidates) {
    function keep(ids, cands) {
      var known = {};
      cands.forEach(function (c) { known[c.id] = true; });
      return (ids || []).filter(function (id, i, all) { return known[id] && all.indexOf(id) === i; });
    }
    return {
      products: keep(draft && draft.featuredProductIds, candidates.products || []),
      collections: keep(draft && draft.collectionIds, candidates.collections || []),
      looks: keep(draft && draft.communityLookIds, candidates.looks || [])
    };
  }

  function buildPayload(brandId, sel) {
    return { brandId: brandId, featuredProductIds: sel.products.slice(), collectionIds: sel.collections.slice(), communityLookIds: sel.looks.slice() };
  }

  // order-insensitive signature to know whether the selection changed since the last save/send
  function signature(sel) {
    function s(list) { return list.slice().sort().join(','); }
    return [s(sel.products), s(sel.collections), s(sel.looks)].join('|');
  }

  /**
   * sel: { products[], collections[], looks[] }; limits: { productsMin, productsMax, looksMax }
   * mode 'send' also enforces the minimum of featured products; 'draft' only the upper limits.
   * -> { products?: 'min'|'max', looks?: 'max' }
   */
  function validateHub(sel, limits, mode) {
    var errors = {};
    if (sel.products.length > limits.productsMax) errors.products = 'max';
    else if (mode === 'send' && sel.products.length < limits.productsMin) errors.products = 'min';
    if (sel.looks.length > limits.looksMax) errors.looks = 'max';
    return errors;
  }

  function firstInvalid(errors) {
    var order = ['products', 'looks'];
    for (var i = 0; i < order.length; i++) if (errors[order[i]]) return order[i];
    return null;
  }

  // Looks order: 0 = gateway order ("Para ti"), 1 = gateway order (no date to rank by: UNKNOWN), 2 = by likes
  function sortLooks(looks, mode) {
    var list = looks.slice();
    if (mode === 2) list.sort(function (a, b) { return (b.likes || 0) - (a.likes || 0); });
    return list;
  }

  // index of the error message for a gateway error: 0 auth, 1 forbidden, 2 conflict, 3 network, 4 server, 5 validation
  function errorIndex(err) {
    var k = err && err.kind;
    if (k === 'unauthenticated') return 0;
    if (k === 'forbidden') return 1;
    if (k === 'conflict') return 2;
    if (k === 'network') return 3;
    if (k === 'validation') return 5;
    return 4;
  }

  // R-7 route B (Javi, 2026-10-22): /pages/hub-marca?marca=<slug>. Index of that brand in the list, 0 when the slug is
  // missing, not a plain slug or not one of the brands
  function brandIndex(brands, search) {
    var m = /[?&]marca=([^&#]*)/.exec(search || '');
    var v = m ? decodeURIComponent(m[1]).trim().toLowerCase() : '';
    if (!/^[a-z0-9-]{1,60}$/.test(v)) return 0;
    for (var i = 0; i < (brands || []).length; i++) if (brands[i] && brands[i].slug === v) return i;
    return 0;
  }

  // the same search with ?marca= set to slug, every other parameter (nc-scenario, ...) kept
  function withBrand(search, slug) {
    var parts = String(search || '').replace(/^\?/, '').split('&').filter(function (p) { return p && !/^marca=/.test(p); });
    parts.push('marca=' + encodeURIComponent(slug));
    return '?' + parts.join('&');
  }

  // read errors: 401 / 403 are a permission panel, everything else a retryable error panel
  function readBlock(err) {
    var k = err && err.kind;
    return k === 'unauthenticated' || k === 'forbidden' ? 'permission' : 'error';
  }

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { fill: fill, plural: plural, setMember: setMember, applyDraft: applyDraft, buildPayload: buildPayload, signature: signature, validateHub: validateHub, firstInvalid: firstInvalid, sortLooks: sortLooks, errorIndex: errorIndex, readBlock: readBlock, brandIndex: brandIndex, withBrand: withBrand };
  }
  if (typeof customElements === 'undefined' || customElements.get('nc-brand-hub-screen')) return;

  // the product page of a garment through the route map (NcNav, /products/<handle>); '' when there is none
  function productHref(id) {
    try { return (typeof window !== 'undefined' && window.NcNav && window.NcNav.url && window.NcNav.url('product', { product: id })) || ''; } catch (e) { return ''; }
  }
  function isRealHref(href) { return !!href && href !== '#' && href.charAt(0) !== '#'; }
  function noop() {}
  function split(text) { return String(text || '').split('|').map(function (x) { return x.trim(); }); }

  class NcBrandHubScreen extends HTMLElement {
    connectedCallback() {
      var self = this;
      this.gw = window.NcApp.gateway();
      this.req = 0;
      this.brandReq = 0;
      this.busy = false;
      this.editing = this.getAttribute('data-editing') === 'true';
      this.brands = [];
      this.index = 0;
      this.sessions = {}; // brand id -> { sel, baseline, saved, sent }
      this.prices = {};
      this.sortMode = 0;
      this.catalog = {}; // product id -> { id, title, brand, price, assetId, slot }: what the Probar chip hands the room
      this.tryCopy = split(this.getAttribute('data-msg-try'));
      this.room = this.querySelector('nc-fitting-room');
      this.limits = {
        productsMin: parseInt(this.getAttribute('data-products-min'), 10) || 0,
        productsMax: parseInt(this.getAttribute('data-products-max'), 10) || 6,
        looksMax: parseInt(this.getAttribute('data-looks-max'), 10) || 6
      };
      this.t = {
        status: split(this.getAttribute('data-msg-status')),
        counts: split(this.getAttribute('data-msg-counts')),
        validation: split(this.getAttribute('data-msg-validation')),
        outcomes: split(this.getAttribute('data-msg-outcomes')),
        errors: split(this.getAttribute('data-msg-errors')),
        chip: split(this.getAttribute('data-msg-chip')),
        permission: split(this.getAttribute('data-msg-permission'))
      };
      this.blocks = {};
      ['skeleton', 'empty', 'permission', 'error', 'content'].forEach(function (b) { self.blocks[b] = self.querySelector('[data-block="' + b + '"]'); });
      this.stateEl = this.querySelector('[data-bh-state]');
      this.statusEl = this.querySelector('[data-bh-status]');
      this.banner = this.querySelector('[data-bh-banner]');
      this.bannerText = this.querySelector('[data-bh-banner-text]');
      this.bannerAction = this.querySelector('[data-bh-banner-action]');
      this.saveBtn = this.querySelector('[data-bh-save] .nc-button');
      this.sendBtn = this.querySelector('[data-bh-send] .nc-button');

      this.addEventListener('click', this.onClick.bind(this));
      this.addEventListener('nc-chip:toggle', this.onChip.bind(this));
      this.addEventListener('nc-bsw:change', this.onBrand.bind(this));
      // the component's cancelable events become this screen's Phase B hooks (preventDefault is passed back)
      this.addEventListener('nc-fitting:connect', function (e) { if (!self.relay('connect', e.detail)) e.preventDefault(); });
      this.addEventListener('nc-fitting:tool', function (e) { if (!self.relay('tool', e.detail)) e.preventDefault(); });
      this.addEventListener('nc-fitting:change', function () { self.syncTry(); });
      this.addEventListener('nc-card:toggle', this.onToggle.bind(this));
      ['permission', 'error'].forEach(function (b) {
        if (self.blocks[b]) self.blocks[b].addEventListener('click', function (e) { if (e.target.closest('[data-nc-retry]')) self.load(); });
      });
      customElements.whenDefined('nc-fitting-room').then(function () { self.load(); });
      if (this.editing) this.gw.getAuthState().then(function (a) {
        var notice = self.querySelector('[data-bh-notice]');
        if (notice && a && a.status !== 'authenticated') notice.hidden = false;
      }, noop);
    }

    relay(name, detail) {
      return this.dispatchEvent(new CustomEvent('nc-brand-hub:' + name, { bubbles: true, cancelable: true, detail: detail || {} }));
    }

    attr(name, fallback) { var v = this.getAttribute(name); return v === null || v === '' ? (fallback || '') : v; }

    q(sel) { return this.querySelector(sel); }

    show(name) {
      var self = this;
      Object.keys(this.blocks).forEach(function (k) { if (self.blocks[k]) self.blocks[k].hidden = k !== name; });
      this.setActionsEnabled(name === 'content');
    }

    setActionsEnabled(on) {
      [this.saveBtn, this.sendBtn].forEach(function (b) {
        if (!b) return;
        if (on) b.removeAttribute('aria-disabled'); else b.setAttribute('aria-disabled', 'true');
      });
    }

    announce(text) { if (this.statusEl) this.statusEl.textContent = text; }

    toast(message, tone, detail) {
      if (window.NcToast) window.NcToast.show({ tone: tone || 'error', message: message, detail: detail });
    }

    get brand() { return this.brands[this.index]; }

    get cur() { return this.brand ? this.sessions[this.brand.id] : null; }

    // ---- load (brands, then the active brand) ----
    load() {
      var self = this;
      var seq = ++this.req;
      this.show('skeleton');
      this.hideBanner();
      this.gw.listBrands({}).then(function (res) {
        if (seq !== self.req) return;
        self.brands = res.items || [];
        if (!self.brands.length) { self.show('empty'); self.announce(self.attr('data-msg-empty')); return; }
        self.index = brandIndex(self.brands, window.location.search);
        self.buildCarousel();
        return self.loadBrand(self.index, seq);
      }, function (err) { if (seq === self.req) self.readFailed(err); });
    }

    readFailed(err) {
      var block = readBlock(err);
      var p = this.t.permission;
      if (block === 'permission') {
        var panel = this.blocks.permission;
        var title = panel.querySelector('.nc-state-panel__title');
        var body = panel.querySelector('.nc-state-panel__body');
        var expired = /caduc/i.test((err && err.message) || '');
        if (err.kind === 'forbidden') { title.textContent = p[2] || title.textContent; }
        else { title.textContent = (expired ? p[1] : p[0]) || title.textContent; }
      }
      this.show(block);
      var el = this.blocks[block].querySelector('[role="alert"]');
      if (el) { el.setAttribute('tabindex', '-1'); el.focus({ preventScroll: true }); }
    }

    // reads the brand hub and its draft (or reuses the unsaved session of that brand) and renders the lists
    loadBrand(i, outerSeq) {
      var self = this;
      var seq = ++this.brandReq;
      var brand = this.brands[i];
      var cached = this.sessions[brand.id];
      return Promise.all([this.gw.getBrandHub(brand.id), cached || !this.editing ? Promise.resolve(null) : this.gw.getBrandHubDraft(brand.id)]).then(function (res) {
        if (seq !== self.brandReq || (outerSeq && outerSeq !== self.req)) return;
        var hub = res[0];
        self.hub = hub;
        self.cand = { products: hub.featuredProducts || [], collections: hub.collections || [], looks: hub.communityLooks || [] };
        if (!cached) {
          var sel = applyDraft(res[1], self.cand);
          self.sessions[brand.id] = { sel: sel, baseline: signature(sel), saved: false, sent: false };
        }
        self.index = i;
        self.syncUrl();
        self.renderAll();
        self.show('content');
        self.syncCarousel();
      }, function (err) {
        if (seq !== self.brandReq || (outerSeq && outerSeq !== self.req)) return;
        self.readFailed(err);
      });
    }

    // ---- carousel (Swiper cards, assets/nc-brand-swiper.js; one slide per brand, built from the carousel templates) ----
    // The DOM is built while the content block is still hidden; Swiper needs real sizes, so it is mounted in mountCarousel()
    // right after the block is shown.
    buildCarousel() {
      if (this.carousel && this.carousel.destroy) this.carousel.destroy();
      this.carousel = null;
      var tpl = this.q('template[data-bh-tpl="carousel"]');
      var slideTpl = this.q('template[data-bh-tpl="carousel-slide"]');
      var root = tpl.content.querySelector('.nc-bsw').cloneNode(true);
      var wrapper = root.querySelector('.swiper-wrapper');
      var n = this.brands.length;
      var self = this;
      var slidePattern = this.attr('data-msg-slide');
      var start = this.index;
      this.brands.forEach(function (b, i) {
        var slide = slideTpl.content.querySelector('.swiper-slide').cloneNode(true);
        slide.setAttribute('data-name', b.name);
        slide.style.setProperty('--bsw-i', String(Math.min(Math.abs(i - start), 4)));
        slide.querySelector('.nc-bsw__name').textContent = b.name;
        var tag = slide.querySelector('.nc-bsw__tagline');
        if (b.tagline) tag.textContent = b.tagline; else tag.remove();
        // "Ver marca" opens the brand's Branding Corner (R-2, nc-route brand_corner via NcNav); no route, no link
        var link = slide.querySelector('.nc-bsw__cta');
        var corner = window.NcNav && window.NcNav.url ? window.NcNav.url('brand_corner', { slug: b.slug }) : '';
        if (corner) link.setAttribute('href', corner); else link.remove();
        slide.setAttribute('aria-label', fill(slidePattern, { i: i + 1, n: n, name: b.name }));
        wrapper.appendChild(slide);
        // cover rule (snippets/nc-store-brand-json.liquid): campaign photo, else the brand's product photo, else the logo on the light backdrop
        var cover = b.coverKind && b.coverKind !== 'logo' && b.coverAssetId ? b.coverAssetId : null;
        slide.setAttribute('data-cover', cover ? b.coverKind : (b.source === 'store' ? 'logo' : 'photo')); // 'photo' = the mock's own brand photos
        // only the starting brand's image is eager (LCP candidate); the others load lazily
        self.applyAsset(slide.querySelector('.nc-bsw__media'), cover || b.heroAssetId, b.name, i === start, !cover && b.source === 'store');
      });
      if (n < 2) {
        var controls = root.querySelector('.nc-bsw__controls');
        if (controls) controls.remove();
        root.classList.add('nc-bsw--single');
      }
      var host = this.q('[data-bh-carousel]');
      host.textContent = '';
      host.appendChild(root);
      this.carouselRoot = root;
    }

    mountCarousel() {
      if (this.carousel || !this.carouselRoot || !window.NcBrandSwiper) return;
      this.carousel = window.NcBrandSwiper.mount(this.carouselRoot, {
        index: this.index,
        labels: {
          prev: this.attr('data-msg-slide-prev', 'Marca anterior'),
          next: this.attr('data-msg-slide-next', 'Marca siguiente'),
          pause: this.attr('data-msg-slide-pause', 'Pausar rotación'),
          play: this.attr('data-msg-slide-play', 'Reanudar rotación'),
          dot: this.attr('data-msg-slide-dot', 'Ir a {name}')
        }
      });
    }

    // keeps ?marca= on the active brand so the address can be shared; replaceState, no navigation (real routing is Phase B)
    syncUrl() {
      var slug = this.brand && this.brand.slug;
      if (!slug || !window.history || !window.history.replaceState) return;
      var next = withBrand(window.location.search, slug);
      if (next !== window.location.search) window.history.replaceState(window.history.state, '', window.location.pathname + next + window.location.hash);
    }

    syncCarousel() {
      this.mountCarousel();
      if (this.carousel && this.carousel.show && this.carousel.index !== this.index) this.carousel.show(this.index);
    }

    onBrand(event) {
      var idx = event.detail.index - 1;
      if (idx === this.index) return;
      if (this.busy) { this.syncCarousel(); return; }
      this.hideBanner();
      this.loadBrand(idx);
    }

    // images enter only through the gateway's asset records; no src (placeholder) leaves the neutral slot
    // `logo`: the image is a brand logo (store brands), shown whole on the stage instead of cropped like a photo
    applyAsset(slot, assetId, alt, eager, logo) {
      if (!slot || !assetId || !this.gw.getAsset) return;
      this.gw.getAsset(assetId).then(function (a) {
        if (!a || !a.src || !slot.isConnected) return;
        var img = document.createElement('img');
        img.src = a.src;
        if (a.width) img.width = a.width;
        if (a.height) img.height = a.height;
        img.alt = a.alt || alt || '';
        img.loading = eager ? 'eager' : 'lazy';
        if (logo) img.classList.add('nc-brand-hub__logo'); // shown whole on a light backdrop (section-nc-brand-hub.css)
        slot.insertBefore(img, slot.firstChild);
      }, noop);
    }

    // ---- render the three candidate lists ----
    renderAll() {
      this.renderProducts();
      if (this.editing) this.renderCollections();
      this.renderLooks();
      this.refresh();
    }

    pick(li, label, title, pressed) {
      if (!this.editing) return;
      var main = li.querySelector('.nc-brand-hub__pick .nc-chip__main');
      main.textContent = label;
      main.setAttribute('aria-label', fill(this.t.chip[2], { name: title }));
      main.setAttribute('aria-pressed', pressed ? 'true' : 'false');
    }

    renderProducts() {
      var self = this;
      var grid = this.q('[data-bh-products]');
      grid.textContent = '';
      var tpl = this.q('template[data-bh-tpl="product"]');
      var sel = this.cur.sel.products;
      this.cand.products.forEach(function (p, i) {
        self.prices[p.id] = p.price;
        self.catalog[p.id] = { id: p.id, title: p.title, brand: p.brand, price: p.price, assetId: p.assetId, slot: p.slot };
        var li = tpl.content.querySelector('li').cloneNode(true);
        li.setAttribute('data-id', p.id);
        var card = li.querySelector('.nc-product-card');
        card.querySelector('.nc-product-card__brand').textContent = p.brand;
        // analytics: the card carries its brand slug so item_impression has a brand (a card without it biases M04 and M12)
        if ((p.brandInfo && p.brandInfo.slug) || p.brandSlug) card.setAttribute('data-nc-brand', (p.brandInfo && p.brandInfo.slug) || p.brandSlug);
        var link = card.querySelector('.nc-card__link');
        link.textContent = p.title;
        link.setAttribute('data-kind', 'product');
        link.setAttribute('data-id', p.id);
        var pHref = productHref(p.id);
        if (pHref) link.setAttribute('href', pHref);
        card.querySelector('.nc-product-card__price').textContent = p.price;
        var fav = card.querySelector('.nc-toggle--favorite');
        fav.setAttribute('data-id', p.id);
        fav.setAttribute('aria-pressed', p.favorite ? 'true' : 'false');
        fav.setAttribute('aria-label', (fav.getAttribute('aria-label') || '').replace('__TITLE__', p.title));
        var swatches = Array.prototype.slice.call(card.querySelectorAll('.nc-product-card__swatch'));
        swatches.forEach(function (sw, k) { if (p.colors && p.colors[k]) sw.style.background = p.colors[k]; else sw.remove(); });
        var wrap = card.querySelector('.nc-product-card__swatches');
        if (wrap && !wrap.children.length) wrap.remove();
        var tryChip = li.querySelector('[data-bh-try] nc-chip');
        if (tryChip) tryChip.setAttribute('data-value', p.id);
        self.pick(li, self.t.chip[0], p.title, sel.indexOf(p.id) >= 0);
        grid.appendChild(li);
        self.applyAsset(card.querySelector('.nc-card__media'), p.assetId, p.title, i < 4);
      });
      this.q('[data-bh-products-empty]').hidden = this.cand.products.length > 0;
      this.syncTry();
    }

    // ---- "Probar": the probador's chooser chip on each product card (shared code path of the component core) ----
    tryChipCopy() {
      var c = this.tryCopy;
      return { add: c[0], inLook: c[1], change: c[2], addAria: c[3], changeAria: c[4] };
    }

    syncTry() {
      var Core = window.NcFittingRoomCore;
      if (!Core || !Core.syncChooserChips) return;
      Core.syncChooserChips(this.querySelectorAll('[data-bh-products] [data-bh-try] nc-chip'), this.room, this.catalog, this.tryChipCopy());
    }

    renderCollections() {
      var self = this;
      var wrap = this.q('[data-bh-collections]');
      wrap.textContent = '';
      var tpl = this.q('template[data-bh-tpl="collection"]');
      var sel = this.cur.sel.collections;
      this.cand.collections.forEach(function (c) {
        var chip = tpl.content.querySelector('nc-chip').cloneNode(true);
        chip.setAttribute('data-id', c.id);
        var main = chip.querySelector('.nc-chip__main');
        main.textContent = c.title;
        main.setAttribute('aria-label', fill(self.t.chip[3], { name: c.title }));
        main.setAttribute('aria-pressed', sel.indexOf(c.id) >= 0 ? 'true' : 'false');
        wrap.appendChild(chip);
      });
      this.q('[data-bh-collections-empty]').hidden = this.cand.collections.length > 0;
      wrap.hidden = !this.cand.collections.length;
    }

    renderLooks() {
      var self = this;
      var grid = this.q('[data-bh-looks]');
      grid.textContent = '';
      var tpl = this.q('template[data-bh-tpl="look"]');
      var sel = this.cur.sel.looks;
      sortLooks(this.cand.looks, this.sortMode).forEach(function (l) {
        var li = tpl.content.querySelector('li').cloneNode(true);
        li.setAttribute('data-id', l.id);
        var card = li.querySelector('.nc-community-card');
        var link = card.querySelector('.nc-card__link');
        link.textContent = l.author;
        link.setAttribute('data-kind', 'look');
        link.setAttribute('data-id', l.id);
        Array.prototype.forEach.call(card.querySelectorAll('[aria-label*="__HANDLE__"]'), function (el) {
          el.setAttribute('aria-label', el.getAttribute('aria-label').split('__HANDLE__').join(l.author));
        });
        card.querySelector('.nc-community-card__tags').textContent = (l.tags || []).join(' ');
        var like = card.querySelector('.nc-toggle--like');
        like.setAttribute('data-id', l.id);
        like.setAttribute('data-count', String(l.likes));
        like.setAttribute('data-initial-pressed', l.liked ? 'true' : 'false');
        like.setAttribute('aria-pressed', l.liked ? 'true' : 'false');
        var count = like.querySelector('[data-nc-count]');
        if (count) count.textContent = String(l.likes);
        var save = card.querySelector('.nc-toggle--save');
        save.setAttribute('data-id', l.id);
        save.setAttribute('aria-pressed', l.saved ? 'true' : 'false');
        self.pick(li, self.t.chip[1], l.author, sel.indexOf(l.id) >= 0);
        grid.appendChild(li);
        self.applyAsset(card.querySelector('.nc-card__media'), l.assetId, l.title, false);
      });
      this.q('[data-bh-looks-empty]').hidden = this.cand.looks.length > 0;
    }

    // meters, status line and button states follow the selection
    refresh() {
      if (!this.editing) return;
      var sel = this.cur.sel;
      this.q('[data-bh-products-meter]').textContent = fill(this.t.counts[0], { n: sel.products.length, m: this.cand.products.length });
      this.q('[data-bh-looks-meter]').textContent = fill(this.t.counts[2], { n: sel.looks.length, m: this.cand.looks.length });
      var cm = this.q('[data-bh-collections-meter]');
      if (cm) cm.textContent = fill(this.t.counts[1], { n: sel.collections.length, m: this.cand.collections.length });
      this.stateEl.textContent = this.t.status[this.stateKey()] || '';
    }

    stateKey() {
      var c = this.cur;
      if (signature(c.sel) !== c.baseline) return 1;
      if (c.sent) return 3;
      if (c.saved) return 2;
      return 0;
    }

    // ---- selection ----
    onChip(event) {
      var chip = event.target.closest('nc-chip');
      if (!chip) return;
      var pressed = event.detail.pressed;
      if (chip.closest('[data-bh-try]')) {
        var Core = window.NcFittingRoomCore;
        if (Core && Core.toggleChooserChip) Core.toggleChooserChip(this.room, this.catalog, chip, pressed);
        this.syncTry();
        return;
      }
      var sorts = chip.closest('[data-bh-sorts]');
      if (sorts) {
        var main = chip.querySelector('.nc-chip__main');
        sorts.querySelectorAll('.nc-chip__main').forEach(function (b) { b.setAttribute('aria-pressed', b === main ? 'true' : 'false'); });
        this.sortMode = Array.prototype.indexOf.call(sorts.querySelectorAll('nc-chip'), chip);
        this.renderLooks();
        return;
      }
      if (!this.editing) return;
      var sel = this.cur.sel;
      var li = chip.closest('li[data-id]');
      if (li && li.parentElement.hasAttribute('data-bh-products')) sel.products = setMember(sel.products, li.getAttribute('data-id'), pressed);
      else if (li && li.parentElement.hasAttribute('data-bh-looks')) sel.looks = setMember(sel.looks, li.getAttribute('data-id'), pressed);
      else if (chip.closest('[data-bh-collections]')) sel.collections = setMember(sel.collections, chip.getAttribute('data-id'), pressed);
      else return;
      this.touched();
    }

    // any edit after an outcome puts the buttons back to default and clears the previous errors
    touched() {
      [this.saveBtn, this.sendBtn].forEach(function (b) {
        var st = b && b.getAttribute('data-state');
        if (st === 'success' || st === 'error') b.setAttribute('data-state', 'default');
      });
      this.clearErrors();
      this.hideBanner();
      this.refresh();
    }

    clearErrors() {
      this.querySelectorAll('[data-bh-error]').forEach(function (e) { e.hidden = true; e.textContent = ''; });
    }

    showErrors(errors) {
      var msg = this.t.validation;
      var lim = this.limits;
      var map = {
        products: errors.products === 'min' ? fill(plural(msg[0], lim.productsMin), { n: lim.productsMin }) : fill(plural(msg[1], lim.productsMax), { n: lim.productsMax }),
        looks: fill(plural(msg[2], lim.looksMax), { n: lim.looksMax })
      };
      this.clearErrors();
      var self = this;
      Object.keys(errors).forEach(function (k) {
        var el = self.q('[data-bh-error="' + k + '"]');
        if (el) { el.textContent = map[k]; el.hidden = false; }
      });
      var first = firstInvalid(errors);
      var target = first === 'looks' ? this.q('[data-bh-looks] .nc-chip__main') : this.q('[data-bh-products] .nc-chip__main');
      if (!target) target = this.q(first === 'looks' ? '#bh-community-title' : '#bh-products-title');
      if (target) { if (!target.matches('button')) target.setAttribute('tabindex', '-1'); target.focus(); }
    }

    // ---- save draft / send changes (distinct actions) ----
    submit(mode) {
      if (!this.editing || this.busy || !this.cur) return;
      var self = this;
      var send = mode === 'send';
      var c = this.cur;
      var errors = validateHub(c.sel, this.limits, mode);
      if (Object.keys(errors).length) { this.showErrors(errors); return; }
      this.clearErrors();
      this.hideBanner();
      var btn = send ? this.sendBtn : this.saveBtn;
      var brandId = this.brand.id;
      var payload = buildPayload(brandId, c.sel);
      var snapshot = signature(c.sel);
      this.setBusy(true, send ? 'send' : 'save');
      btn.setAttribute('data-state', 'loading');
      (send ? this.gw.publishBrandHub(payload) : this.gw.saveBrandHubDraft(payload)).then(function (res) {
        self.setBusy(false);
        btn.setAttribute('data-state', 'success');
        var s = self.sessions[brandId];
        if (s) {
          s.baseline = snapshot;
          s.saved = !send;
          s.sent = send;
        }
        if (self.brand && self.brand.id === brandId) self.refresh();
        var o = self.t.outcomes;
        self.toast(send ? o[2] : o[0], 'success', send ? o[3] : o[1]);
        self.dispatchEvent(new CustomEvent(send ? 'nc-brand-hub:published' : 'nc-brand-hub:draft-saved', { bubbles: true, detail: { brandId: brandId, status: res && res.status } }));
      }, function (err) {
        self.setBusy(false);
        var i = errorIndex(err);
        var detail = [];
        if (i === 5 && err.fieldErrors) Object.keys(err.fieldErrors).forEach(function (k) { if (typeof err.fieldErrors[k] === 'string' && k !== '_') detail.push(err.fieldErrors[k]); });
        // the selection is never cleared: the same button retries; a conflict offers to reload the hub
        btn.setAttribute('data-state', i === 3 || i === 4 ? 'error' : 'default');
        self.showBanner([self.t.errors[i]].concat(detail).join(' '), i === 2);
      });
    }

    setBusy(on, which) {
      this.busy = on;
      var self = this;
      [[this.saveBtn, 'save'], [this.sendBtn, 'send']].forEach(function (p) {
        if (!p[0] || (on && p[1] === which)) return;
        if (on) p[0].setAttribute('aria-disabled', 'true'); else if (self.blocks.content && !self.blocks.content.hidden) p[0].removeAttribute('aria-disabled');
      });
    }

    showBanner(text, reload) {
      this.bannerText.textContent = text;
      this.bannerAction.hidden = !reload;
      this.banner.hidden = false;
    }

    hideBanner() {
      if (this.banner) this.banner.hidden = true;
    }

    reloadBrand() {
      if (!this.brand) return;
      delete this.sessions[this.brand.id];
      this.hideBanner();
      var self = this;
      this.loadBrand(this.index).then(function () { var t = self.q('[data-nc-main]'); if (t) t.focus({ preventScroll: true }); });
    }

    // ---- clicks ----
    onClick(event) {
      var t = event.target;
      if (t.closest('[data-bh-save] .nc-button')) { this.submit('save'); return; }
      if (t.closest('[data-bh-send] .nc-button')) { this.submit('send'); return; }
      if (t.closest('[data-bh-banner-action] .nc-button')) { this.reloadBrand(); return; }
      var link = t.closest('.nc-card__link');
      if (link) {
        // a garment card OPENS its product page (Javi 2026-10-31: tapping a garment must not put it in the look); only the
        // "Probar" chip on the card adds it. A real href navigates natively; otherwise the route map resolves it from the event.
        if (link.getAttribute('data-kind') === 'product' && isRealHref(link.getAttribute('href'))) return;
        event.preventDefault();
        this.dispatchEvent(new CustomEvent('nc-brand-hub:open', { bubbles: true, detail: { kind: link.getAttribute('data-kind'), id: link.getAttribute('data-id') } }));
      }
    }

    // a saved toggle is written back to the loaded candidates, so re-sorting the looks does not undo it
    remember(d) {
      var list = !this.cand ? [] : d.kind === 'favorite' ? this.cand.products : this.cand.looks;
      var item = list.filter(function (x) { return x.id === d.id; })[0];
      if (!item) return;
      if (d.kind === 'favorite') item.favorite = d.pressed;
      else if (d.kind === 'save') item.saved = d.pressed;
      else if (d.kind === 'like' && item.liked !== d.pressed) { item.likes = (item.likes || 0) + (d.pressed ? 1 : -1); item.liked = d.pressed; }
    }

    // card toggles (favorite, like, save): gateway first feedback through NcCard
    onToggle(event) {
      var d = event.detail;
      var btn = event.target.closest('.nc-toggle');
      if (!btn) return;
      var self = this;
      var call = d.kind === 'favorite' ? this.gw.setFavorite({ entity: 'product', id: d.id, value: d.pressed })
        : d.kind === 'like' ? this.gw.setLookLike({ lookId: d.id, value: d.pressed })
        : d.kind === 'save' ? this.gw.setLookSaved({ lookId: d.id, value: d.pressed }) : null;
      if (!call) return;
      window.NcCard.setState(btn, 'loading');
      call.then(function () {
        window.NcCard.setState(btn, 'default');
        self.remember(d);
      }, function () {
        window.NcCard.setState(btn, 'error', d.kind === 'favorite' ? self.attr('data-msg-favorite-error') : self.attr('data-msg-look-error'));
      });
    }
  }

  customElements.define('nc-brand-hub-screen', NcBrandHubScreen);
})();
